import { inject, Injectable } from "@angular/core";
import {
  AuthenticationDetails,
  ChallengeName,
  CognitoUser,
  CognitoUserSession,
} from "amazon-cognito-identity-js";
import {
  BehaviorSubject,
  distinctUntilChanged,
  filter,
  from,
  Observable,
  Observer,
  switchMap,
  take,
} from "rxjs";
import {
  ConfirmPasswordEnum,
  ForgotPasswordEnum,
  ResetPasswordEnum,
  SignInEnum,
  SignUpEnum,
} from "../enums/authenticate.enum";
import {
  CognitoConfirmPasswordResult,
  CognitoForgotPasswordResult,
  CognitoResetPasswordResult,
  CognitoSignInResult,
  CognitoSignUpResult,
} from "../interfaces/auth.interfaces";
import { CognitoUtilsService } from "./cognito-utils.service";
import { LoggedUserService } from "../../core/services/logged-user.service";
import { AnalyticsService } from "../../core/services/analytics.service";

@Injectable({
  providedIn: "root",
})
export class AuthService {
  private readonly _cognitoUtilsService = inject(CognitoUtilsService);
  private readonly _loggedUserService = inject(LoggedUserService);
  private readonly _analyticsService = inject(AnalyticsService);

  private readonly _isAuthenticatedSubject = new BehaviorSubject(false);
  private readonly _isAuthenticated$ = this._isAuthenticatedSubject.pipe(
    distinctUntilChanged(),
  );
  private readonly _isLoadingSubject = new BehaviorSubject(false);
  private readonly _isLoading$ = this._isLoadingSubject.pipe(
    distinctUntilChanged(),
  );

  cognitoUser: CognitoUser | undefined;

  // Temporary storage for auto-login after OTP confirmation or force change password
  private _pendingCredentials: { email: string; password: string } | null =
    null;

  /**
   * Returns true if there are pending credentials stored (for force change password flow)
   */
  get hasPendingCredentials(): boolean {
    return this._pendingCredentials !== null;
  }

  /**
   * Clears pending credentials
   */
  clearPendingCredentials(): void {
    this._pendingCredentials = null;
  }

  constructor() {
    void this._checkUserSession();
  }

  public signIn(
    email: string,
    password: string,
  ): Observable<CognitoSignInResult> {
    const authenticationDetails = new AuthenticationDetails({
      Username: email,
      Password: password,
    });

    this.cognitoUser = new CognitoUser({
      Username: email,
      Pool: this._cognitoUtilsService.getUserPool(),
    });

    return new Observable<CognitoSignInResult>(
      (observer: Observer<CognitoSignInResult>) => {
        this.cognitoUser?.authenticateUser(authenticationDetails, {
          onSuccess: (result: CognitoUserSession) => {
            this._onLoginSuccess(result, observer);
          },
          // First time login attempt - store credentials for force change password flow
          newPasswordRequired: () => {
            this._pendingCredentials = { email, password };
            observer.next({ type: SignInEnum.NEW_PASSWORD_REQUIRED });
            observer.complete();
          },
          totpRequired: (
            challengeName: ChallengeName,
            challengeParameters: Record<string, string>,
          ) => {
            observer.next({
              type: SignInEnum.TOTP_REQUIRED,
              result: {
                challengeName,
                challengeParameters,
              },
            });
            observer.complete();
          },
          onFailure: (error: Error) => {
            observer.error({ type: SignInEnum.FAIL, result: error });
          },
        });
      },
    );
  }

  forgotPassword(username: string): Observable<CognitoForgotPasswordResult> {
    const userData = {
      Username: username,
      Pool: this._cognitoUtilsService.getUserPool(),
    };

    const cognitoUser = new CognitoUser(userData);

    return new Observable<CognitoForgotPasswordResult>((observer) => {
      cognitoUser.forgotPassword({
        onSuccess: () => {
          observer.next({ type: ForgotPasswordEnum.SUCCESS });
          observer.complete();
        },
        inputVerificationCode: () => {
          observer.next({ type: ForgotPasswordEnum.INPUT_VERIFICATION_CODE });
          observer.complete();
        },
        onFailure: (error) => {
          observer.error({ type: ForgotPasswordEnum.FAIL, result: error });
        },
      });
    });
  }

  resetPassword(
    email: string,
    temporaryPassword: string,
    newPassword: string,
  ): Observable<CognitoResetPasswordResult> {
    return this.signIn(email, temporaryPassword).pipe(
      filter((event) => event.type === SignInEnum.NEW_PASSWORD_REQUIRED),
      switchMap(
        () =>
          new Observable<CognitoResetPasswordResult>((observer) => {
            this.cognitoUser?.completeNewPasswordChallenge(
              newPassword,
              undefined,
              {
                onSuccess: (session: CognitoUserSession) => {
                  void this._checkUserSession().then(() => {
                    observer.next({
                      type: ResetPasswordEnum.SUCCESS,
                      result: session,
                    });
                    observer.complete();
                  });
                },
                onFailure: (error: Error) => {
                  observer.error({ type: SignInEnum.FAIL, result: error });
                },
              },
            );
          }),
      ),
    );
  }

  /**
   * Resets password using stored credentials from the login attempt.
   * Used when user needs to change password on first login (force change password flow).
   * @param newPassword - The new password
   * @returns Observable that completes when password is reset
   */
  resetPasswordWithStoredCredentials(
    newPassword: string,
  ): Observable<CognitoResetPasswordResult> {
    if (!this._pendingCredentials) {
      return new Observable((observer) => {
        observer.error({
          type: ResetPasswordEnum.FAIL,
          result: new Error("No stored credentials available"),
        });
      });
    }

    const { email, password } = this._pendingCredentials;

    return this.resetPassword(email, password, newPassword).pipe(
      switchMap((result) => {
        this._pendingCredentials = null; // Clear credentials after successful reset
        return new Observable<CognitoResetPasswordResult>((observer) => {
          observer.next(result);
          observer.complete();
        });
      }),
    );
  }

  confirmNewPassword(
    email: string,
    verificationCode: string,
    password: string,
  ): Observable<CognitoConfirmPasswordResult> {
    const userData = {
      Username: email,
      Pool: this._cognitoUtilsService.getUserPool(),
    };

    const cognitoUser = new CognitoUser(userData);

    return new Observable<CognitoConfirmPasswordResult>((observer) => {
      cognitoUser.confirmPassword(verificationCode, password, {
        onSuccess: (result) => {
          observer.next({ type: ConfirmPasswordEnum.SUCCESS, result });
          observer.complete();
        },
        onFailure: (error: Error) => {
          observer.error({ type: ForgotPasswordEnum.FAIL, result: error });
        },
      });
    });
  }

  /**
   * Changes the password for the currently authenticated user
   * @param oldPassword - Current password
   * @param newPassword - New password
   * @returns Observable that completes when password is changed
   */
  changePassword(oldPassword: string, newPassword: string): Observable<void> {
    const user = this._cognitoUtilsService.getCurrentUser();

    if (!user) {
      return new Observable((observer) => {
        observer.error(new Error("No authenticated user"));
      });
    }

    return new Observable<void>((observer) => {
      user.getSession(
        (sessionError: Error | null, session: CognitoUserSession | null) => {
          if (sessionError || !session) {
            observer.error(sessionError || new Error("No valid session"));
            return;
          }

          user.changePassword(oldPassword, newPassword, (error) => {
            if (error) {
              observer.error(error);
            } else {
              observer.next();
              observer.complete();
            }
          });
        },
      );
    });
  }

  /**
   * Logs out the user
   */
  logout(): Observable<void> {
    const user = this._cognitoUtilsService.getCurrentUser();

    if (!user) {
      this._analyticsService.reset();
      this._loggedUserService.clearUser();
      return from(this._checkUserSession());
    }
    return new Observable((observer) => {
      user.signOut(() => {
        this._analyticsService.reset();
        this._loggedUserService.clearUser();
        void this._checkUserSession().then(() => {
          observer.next();
          observer.complete();
        });
      });
    });
  }

  signUp(email: string, password: string): Observable<CognitoSignUpResult> {
    // Store credentials for auto-login after OTP confirmation
    this._pendingCredentials = { email, password };

    return new Observable<CognitoSignUpResult>((observer) => {
      this._cognitoUtilsService
        .getUserPool()
        .signUp(email, password, [], [], (error) => {
          if (error) {
            this._pendingCredentials = null; // Clear on error
            observer.error({ type: SignUpEnum.FAIL, result: error });
          } else {
            observer.next({ type: SignUpEnum.SUCCESS });
            observer.complete();
          }
        });
    });
  }

  confirmSignUp(confirmationCode: string): Observable<void> {
    // if the confirm sign up is called the cognito user must be defined
    return new Observable<void>((observer) => {
      this.cognitoUser?.confirmRegistration(confirmationCode, true, (error) => {
        if (error) {
          observer.error(error);
        } else {
          // Auto-login after successful OTP confirmation
          if (this._pendingCredentials) {
            const { email, password } = this._pendingCredentials;
            this._pendingCredentials = null; // Clear credentials

            this.signIn(email, password)
              .pipe(take(1))
              .subscribe({
                next: () => {
                  observer.next();
                  observer.complete();
                },
                error: () => {
                  // Even if login fails, OTP was confirmed successfully
                  observer.next();
                  observer.complete();
                },
              });
          } else {
            observer.next();
            observer.complete();
          }
        }
      });
    });
  }

  resendCode(): Observable<void> {
    // if the confirm sign up is called the cognito, user must be defined
    return new Observable<void>((observer) => {
      this.cognitoUser?.resendConfirmationCode((error) => {
        if (error) {
          observer.error(error);
        } else {
          observer.next();
          observer.complete();
        }
      });
    });
  }

  /**
   * Checks if the user is authenticated.
   * @returns an observable that emits the result of the authentication check
   */
  public isAuthenticated(): Observable<boolean> {
    // trigger the check manually and return the updated observable
    return from(this._checkUserSession()).pipe(
      switchMap(() => this._isAuthenticated$),
    );
  }

  public isLoading(): Observable<boolean> {
    return this._isLoading$;
  }

  /**
   * Handles the successful login.
   * @param session the user's session
   * @param observer the observer to notify
   */
  private _onLoginSuccess(
    session: CognitoUserSession,
    observer: Observer<CognitoSignInResult>,
  ): void {
    void this._checkUserSession().then(() => {
      observer.next({ type: SignInEnum.SUCCESS, result: session });
      observer.complete();
    });
  }

  /**
   * Checks the user's session.
   */
  private async _checkUserSession(): Promise<void> {
    this._isLoadingSubject.next(true);
    let isValid = false;
    try {
      const session = await this._cognitoUtilsService.getSession();
      isValid = session?.isValid() ?? false;
    } catch {
      // Session check failed - user is not authenticated
    } finally {
      this._isAuthenticatedSubject.next(isValid);
      this._isLoadingSubject.next(false);
    }
  }
}
